import { Button } from '@components/Button'
import ConfirmationModal, { DeletionTarget, Disclaimer } from '@components/ConfirmationModal'
import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import { ErrorNote } from '@components/Notice'
import { PageHeader, PageScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import { SortableRowOverlay, useSortableRowSensors } from '@components/SortableRow'
import { useSocket } from '@context/SocketContext'
import {
	Active,
	Announcements,
	closestCenter,
	DndContext,
	DragEndEvent,
	DragOverEvent,
	DragOverlay,
	DragStartEvent,
	MeasuringStrategy,
	Over,
} from '@dnd-kit/core'
import { useEvents, useIdeas, useTrip, useTripDayInfos, useUpdateTripDayInfo } from '@hooks'
import { formatTimeOfDay } from '@lib/datetime'
import { editTripDestinationsPath } from '@pages/Trips/EditTripDestinationsFlow/paths'
import { Event } from '@types'
import toast from 'react-hot-toast'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ActionMenuItem } from '../ActionMenu'
import { useOpenEventCreate } from '../EventCreateFlow'
import PickIdeaDialog from '../PickIdeaDialog'
import { tripCalendar } from '../core/calendar'
import { dayHeader, destinationSegments, spanIndexForDay } from '../core/dayHeader'
import { destinationColorIndex, legendRows, tripSummary } from '../core/destinations'
import { groupByDay, placeEvent, Position, positionOf } from '../core/placement'
import EditDayInfoModal from '../EditDayInfoModal'
import DestinationLegend from '../DestinationLegend/DestinationLegend'
import { CalendarColumn } from '../ItineraryPage.styles'
import TripCalendar from '../TripCalendar/TripCalendar'
import TripSummaryHeader from '../TripSummaryHeader/TripSummaryHeader'
import { useEventMutations } from '../useEventMutations'
import { usePlaceEvent } from '../usePlaceEvent'
import { useSelectedDay } from '../useSelectedDay'
import DaySection from './DaySection'
import { dayOfSlotId } from './daySlots'
import { itineraryPath, quickEditPath } from './paths'
import { ListColumn, QuickEditCard, QuickEditColumn } from './QuickEditPage.styles'

const NO_EVENTS: Event[] = []

/**
 * Reach across a long list comes from auto-scroll alone (days never collapse
 * mid-drag), so it starts a little further from the edge than dnd-kit's
 * default and picks up faster. Vertical only.
 */
const AUTO_SCROLL = { threshold: { x: 0, y: 0.25 }, acceleration: 14 }
/** The list changes height as a row crosses between days; stale droppable rects would aim the drop at where a row used to be. */
/** How long a jump's own scroll is ignored by the highlight: a few frames, since the scroll is instant. */
const JUMP_SETTLE_MS = 150
const MEASURING = { droppable: { strategy: MeasuringStrategy.Always } }

/**
 * Where a drag is pointing, as a slot in the day-grouped list: an empty day's
 * slot, or the row under it — past that row when the dragged one has cleared
 * it coming from another day. Within a day the slot is the row's own: the
 * sortable strategy has already shifted the rows between.
 */
const dropPosition = (days: Event[][], from: Position, active: Active, over: Over): Position | null => {
	const emptyDay = dayOfSlotId(over.id)
	if (emptyDay !== null) return { dayIndex: emptyDay, index: days[emptyDay]?.length ?? 0 }
	const target = positionOf(days, Number(over.id))
	if (!target || target.dayIndex === from.dayIndex) return target
	const dragged = active.rect.current.translated
	const below = !!dragged && dragged.top > over.rect.top + over.rect.height
	return { dayIndex: target.dayIndex, index: target.index + (below ? 1 : 0) }
}

/**
 * Quick Edit: every Trip day of the trip as one scrolling list, for placing
 * events rather than reading them. A row moves (within its day or to another)
 * and deletes; a day adds, and opens its name and notes. An event's own fields
 * are not editable here. There is no Save —
 * a drop commits optimistically, and realtime refetches wait for the drop
 * (ADR 0009). The itinerary's selected day is read to open on and never
 * written: the calendar's highlight following the scroll is presentation only.
 */
const QuickEditPage: React.FC = () => {
	const { tripId = '' } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
	const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useEvents(tripId)
	const { data: dayInfosData } = useTripDayInfos(tripId)
	const { data: ideasData } = useIdeas(tripId)
	const { isConnected, joinTrip, leaveTrip, holdRealtime } = useSocket()
	const { removeEvent } = useEventMutations(tripId)
	const place = usePlaceEvent(tripId)
	const updateDayInfo = useUpdateTripDayInfo()
	const sensors = useSortableRowSensors({ holdOnTouch: true })

	const dayCount = trip?.dayCount ?? 0
	const { selectedDay } = useSelectedDay(tripId, trip ? { startDate: trip.startDate, dayCount } : undefined)
	/** The day at the top of the list, once the reader has scrolled or jumped; until then, the day they came from. */
	const [scrolledDay, setScrolledDay] = useState<number | null>(null)
	const viewedDay = scrolledDay ?? selectedDay
	const openEventCreate = useOpenEventCreate(tripId, 'quickEdit')
	/** The day an idea is being picked for, while the idea picker is up. */
	const [pickingIdeaFor, setPickingIdeaFor] = useState<number | null>(null)
	const [deletingEvent, setDeletingEvent] = useState<Event | null>(null)
	const [editingDay, setEditingDay] = useState<number | null>(null)
	/** The list as the drag has it; from the drop until the cache shows the same, so the card never returns to where it was. */
	const [draft, setDraft] = useState<Event[][] | null>(null)
	const [activeEvent, setActiveEvent] = useState<Event | null>(null)

	const groundRef = useRef<HTMLDivElement>(null)
	const listRef = useRef<HTMLDivElement>(null)
	const jumping = useRef(false)
	const jumpTimer = useRef<number | undefined>(undefined)
	const dragging = useRef(false)
	/** The hold of the drag in flight, and of every drop still on its way to the server. */
	const dragHold = useRef<(() => void) | null>(null)
	const commitHolds = useRef(new Set<() => void>())

	useEffect(() => {
		if (!tripId || !isConnected) return
		joinTrip(tripId)
		return () => leaveTrip(tripId)
	}, [tripId, isConnected, joinTrip, leaveTrip])

	// A page left mid-drag or mid-save must not keep the gate shut behind it.
	useEffect(() => {
		const commits = commitHolds.current
		return () => {
			dragHold.current?.()
			commits.forEach(release => release())
		}
	}, [])

	// -- Derivation ----------------------------------------------------------

	const destinationSpans = useMemo(() => (trip ? destinationSegments(trip) : []), [trip])
	const colorIndexOf = useCallback(
		(dayIndex: number) => destinationColorIndex(destinationSpans, dayIndex),
		[destinationSpans],
	)
	const days = useMemo(() => groupByDay(eventsData?.events ?? NO_EVENTS, dayCount), [eventsData, dayCount])
	const shownDays = draft ?? days

	// The cache has caught up with the drop (or rolled it back): the draft has nothing left to say.
	useEffect(() => {
		if (!dragging.current) setDraft(null)
	}, [days])

	const headers = useMemo(
		() =>
			Array.from({ length: dayCount }, (_, dayIndex) =>
				dayHeader({
					dayIndex,
					startDate: trip?.startDate,
					segments: destinationSpans,
					dayName: dayInfosData?.tripDayInfo.find(info => info.dayIndex === dayIndex)?.name,
				}),
			),
		[dayCount, trip?.startDate, destinationSpans, dayInfosData],
	)
	const daysWithEvents = useMemo(
		() => new Set(shownDays.flatMap((day, dayIndex) => (day.length > 0 ? [dayIndex] : []))),
		[shownDays],
	)
	const calendar = useMemo(
		() => tripCalendar(trip?.startDate, dayCount, { selectedDay: viewedDay, daysWithEvents, colorIndexOf }),
		[trip?.startDate, dayCount, viewedDay, daysWithEvents, colorIndexOf],
	)
	const legend = useMemo(() => (trip ? legendRows(trip.destinations, trip.startDate) : []), [trip])
	const summary = useMemo(
		() => tripSummary({ startDate: trip?.startDate, dayCount, destinationCount: trip?.destinations.length ?? 0 }),
		[trip, dayCount],
	)

	// -- Scrolling -----------------------------------------------------------

	const scrollToDay = useCallback((dayIndex: number) => {
		const section = listRef.current?.querySelector(`[data-day-index="${dayIndex}"]`)
		if (!section) return
		// The jump names its own day: the last days of a trip can't reach the top, and the scroll would name another.
		// Held past the jump's own scroll event, which lands a frame later — a frame callback queued here would run before its measurement.
		jumping.current = true
		section.scrollIntoView?.({ block: 'start' })
		window.clearTimeout(jumpTimer.current)
		jumpTimer.current = window.setTimeout(() => (jumping.current = false), JUMP_SETTLE_MS)
	}, [])

	const handleJumpToDay = useCallback(
		(dayIndex: number) => {
			setScrolledDay(dayIndex)
			scrollToDay(dayIndex)
		},
		[scrollToDay],
	)

	// Open where the itinerary was. Day 1 is already there, and scrolling to it would push the stacked calendar off screen.
	const loaded = !!trip && !!eventsData
	useEffect(() => {
		if (loaded && selectedDay > 0) scrollToDay(selectedDay)
		// Once, on open: the selected day does not change while this page is up.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loaded])

	// The day whose header is at the top of whatever scrolls — the list's own pane, or the page once the card has stacked.
	useEffect(() => {
		let frame = 0
		const measure = () => {
			frame = 0
			const list = listRef.current
			const ground = groundRef.current
			if (!list || !ground || jumping.current) return
			const line = Math.max(list.getBoundingClientRect().top, ground.getBoundingClientRect().top) + 1
			let current = 0
			list.querySelectorAll<HTMLElement>('[data-day-index]').forEach(section => {
				if (section.getBoundingClientRect().top <= line) current = Number(section.dataset.dayIndex)
			})
			setScrolledDay(current)
		}
		const onScroll = () => {
			if (!frame) frame = requestAnimationFrame(measure)
		}
		// Scroll events do not bubble; capturing on the window hears both scrollers.
		window.addEventListener('scroll', onScroll, true)
		return () => {
			window.removeEventListener('scroll', onScroll, true)
			if (frame) cancelAnimationFrame(frame)
			window.clearTimeout(jumpTimer.current)
		}
	}, [])

	// -- Dragging ------------------------------------------------------------

	const endDrag = () => {
		dragging.current = false
		setActiveEvent(null)
		dragHold.current?.()
		dragHold.current = null
	}

	const handleDragStart = ({ active }: DragStartEvent) => {
		const at = positionOf(days, Number(active.id))
		if (!at) return
		dragging.current = true
		dragHold.current = holdRealtime()
		setDraft(days)
		setActiveEvent(days[at.dayIndex][at.index])
	}

	// Crossing into another day re-homes the row in the draft, so that day's context makes room for it.
	const handleDragOver = ({ active, over }: DragOverEvent) => {
		if (!over || !draft || over.id === active.id) return
		const from = positionOf(draft, Number(active.id))
		const to = from && dropPosition(draft, from, active, over)
		if (!from || !to || to.dayIndex === from.dayIndex) return
		setDraft(placeEvent(draft, from, to)?.days ?? draft)
	}

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		const eventId = Number(active.id)
		// Let go over nothing: a cancel, whatever day the draft had moved the row to.
		let finalDays = over ? (draft ?? days) : days
		const from = positionOf(finalDays, eventId)
		const to = over && from && over.id !== active.id ? dropPosition(finalDays, from, active, over) : null
		if (from && to) finalDays = placeEvent(finalDays, from, to)?.days ?? finalDays

		// The patch is the difference between the list as the cache has it and the list as the drag left it.
		const source = positionOf(days, eventId)
		const drop = positionOf(finalDays, eventId)
		const result = source && drop ? placeEvent(days, source, drop) : null

		// The server echoes this move back to the room; refetching on it while a later drop is still
		// on its way would answer without that drop. So the hold outlives the drag by its own save.
		if (result) {
			const release = holdRealtime()
			commitHolds.current.add(release)
			place(result.patch, () => {
				commitHolds.current.delete(release)
				release()
			})
		}
		endDrag()
		setDraft(result ? result.days : null)
	}

	const handleDragCancel = () => {
		endDrag()
		setDraft(null)
	}

	const announcements = useMemo<Announcements>(() => {
		const nameOf = (id: string | number) => shownDays.flat().find(e => e.id === Number(id))?.name ?? 'event'
		const dayTitleUnder = (over: Over | null) => {
			if (!over) return null
			const at = dayOfSlotId(over.id) ?? positionOf(shownDays, Number(over.id))?.dayIndex
			return at === undefined ? null : headers[at]?.title
		}
		return {
			onDragStart: ({ active }) => `Picked up ${nameOf(active.id)}.`,
			onDragOver: ({ active, over }) => (dayTitleUnder(over) ? `${nameOf(active.id)} is over ${dayTitleUnder(over)}.` : undefined),
			onDragEnd: ({ active, over }) =>
				dayTitleUnder(over) ? `${nameOf(active.id)} was dropped on ${dayTitleUnder(over)}.` : `${nameOf(active.id)} was dropped.`,
			onDragCancel: ({ active }) => `Moving ${nameOf(active.id)} was cancelled.`,
		}
	}, [shownDays, headers])

	// -- Render --------------------------------------------------------------

	if (tripLoading || eventsLoading) return <PageLoader label="Loading trip details…" />
	if (tripError || eventsError) {
		return <ErrorNote>Error loading trip: {tripError?.message || eventsError?.message}</ErrorNote>
	}
	if (!trip) return <ErrorNote>Trip not found</ErrorNote>

	const openDestinationsEditor = () =>
		navigate(editTripDestinationsPath(tripId), { state: { returnTo: quickEditPath(tripId, viewedDay), returnLabel: 'Quick Edit' } })
	const editingInfo = dayInfosData?.tripDayInfo.find(info => info.dayIndex === editingDay)
	const handleSaveDayInfo = async (name: string | null, note: string | null) => {
		if (editingDay === null) return
		try {
			await updateDayInfo.mutateAsync({ tripId, dayIndex: editingDay, data: { name, note } })
			setEditingDay(null)
			toast.success('Day info updated')
		} catch {
			toast.error('Failed to save day info. Please try again.')
		}
	}
	const hasIdeas = (ideasData?.ideas.length ?? 0) > 0
	const addItemsFor = (dayIndex: number): ActionMenuItem[] => [
		{ label: 'Build from scratch', onSelect: () => openEventCreate(dayIndex) },
		...(hasIdeas ? [{ label: 'Pull from ideas', onSelect: () => setPickingIdeaFor(dayIndex) }] : []),
	]

	return (
		<PageScroll ref={groundRef}>
			<QuickEditColumn>
				<PageHeader
					title="Quick Edit"
					backTo={{ path: itineraryPath(tripId, selectedDay), label: 'Itinerary' }}
					actions={
						<Button type="button" variant="secondary" onClick={openDestinationsEditor}>
							Edit Destinations
						</Button>
					}
				/>
				{dayCount === 0 ? (
					<EmptyState
						art="itinerary"
						title="Set trip dates to begin planning"
						description="You can always change it later."
					>
						<EmptyStateButton onClick={openDestinationsEditor}>Set Trip Dates</EmptyStateButton>
					</EmptyState>
				) : (
					<QuickEditCard>
						<CalendarColumn>
							<TripSummaryHeader summary={summary} onEditDestinations={openDestinationsEditor} />
							<TripCalendar model={calendar} onSelectDay={handleJumpToDay} />
							<DestinationLegend
								rows={legend}
								selectedDay={viewedDay}
								activeIndex={spanIndexForDay(destinationSpans, viewedDay)}
								onSelectDay={handleJumpToDay}
							/>
						</CalendarColumn>
						<ListColumn ref={listRef} aria-label="Every day of the trip">
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								measuring={MEASURING}
								autoScroll={AUTO_SCROLL}
								accessibility={{ announcements }}
								onDragStart={handleDragStart}
								onDragOver={handleDragOver}
								onDragEnd={handleDragEnd}
								onDragCancel={handleDragCancel}
							>
								{shownDays.map((events, dayIndex) => (
									<DaySection
										key={dayIndex}
										dayIndex={dayIndex}
										header={headers[dayIndex]}
										events={events}
										addItems={addItemsFor(dayIndex)}
										onEditDay={() => setEditingDay(dayIndex)}
										onDelete={setDeletingEvent}
									/>
								))}
								<DragOverlay>
									{activeEvent && (
										<SortableRowOverlay name={activeEvent.name} meta={formatTimeOfDay(activeEvent.startTime)} />
									)}
								</DragOverlay>
							</DndContext>
						</ListColumn>
					</QuickEditCard>
				)}
			</QuickEditColumn>

			<PickIdeaDialog tripId={tripId} dayIndex={pickingIdeaFor} from="quickEdit" onClose={() => setPickingIdeaFor(null)} />
			<EditDayInfoModal
				isOpen={editingDay !== null}
				onClose={() => setEditingDay(null)}
				onSave={handleSaveDayInfo}
				isSaving={updateDayInfo.isPending}
				initialName={editingInfo?.name}
				initialNote={editingInfo?.note}
				dayLabel={editingDay === null ? '' : (headers[editingDay]?.subtitle ?? headers[editingDay]?.title ?? '')}
			/>
			<ConfirmationModal
				isOpen={deletingEvent !== null}
				onClose={() => setDeletingEvent(null)}
				onConfirm={() => {
					if (deletingEvent) void removeEvent(deletingEvent.id, deletingEvent.name)
					setDeletingEvent(null)
				}}
				title="Delete Event"
				message={
					<>
						Are you sure you want to delete '<DeletionTarget>{deletingEvent?.name}</DeletionTarget>'?
						<Disclaimer>This action cannot be undone</Disclaimer>
					</>
				}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>
		</PageScroll>
	)
}

export default QuickEditPage
